import { addDays, isBefore } from '@if/core/date'
import { EFinancingProgram, EPaymentFrequency, EPaymentPlan, EProvince } from '@if/core/types'
import { formatCurrency, formatDate, normalizeDateTime } from '@if/core/utils'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ComputeFundingDto as ComputeIfXpressFundingDto } from '@src/types/ComputeIfXpressFundingDto'
import { ComputeFundingResultDto as ComputeIfXpressFundingResultDto } from '@src/types/ComputeIfXpressFundingResultDto'
import { ComputeMonthTermFrequencyDto } from '@src/types/ComputeMonthTermFrequencyDto'
import { ComputeFundingDto } from '@src/types/ComputePersonalLoanFundingDto'
import { ComputeFundingResultDto } from '@src/types/ComputePersonalLoanFundingResultDto'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { MerchantPaymentPlan } from '@src/types/MerchantPaymentPlan'
import {
  EditIfXpressWorksheetDto,
  EditIfXpressWorksheetDtoSchema,
  EditPersonalLoanWorksheetDto,
  EditPersonalLoanWorksheetDtoSchema,
  buildComputePossibleTermsDtoSchema,
  buildWorksheetBaseSchema,
  computePossibleTermsIfXpressDtoSchema,
} from '@src/types/WorksheetSchema'
import { subtract } from 'lodash-es'

export function createNewPersonalLoanWorksheetDto(id: string, data: EditPersonalLoanWorksheetDto) {
  const newWorksheet = {
    ...(EditPersonalLoanWorksheetDtoSchema.getDefault() as unknown as EditPersonalLoanWorksheetDto),
    includeInsurance: data.includeInsurance,
  } as EditPersonalLoanWorksheetDto
  newWorksheet.amountRequested = data.amountRequested
  newWorksheet.deliveryOn = data.deliveryOn
  newWorksheet.firstPaymentOn = data.firstPaymentOn
  newWorksheet.paymentFrequency = data.paymentFrequency
  newWorksheet.term = data.term
  newWorksheet.merchantPayments = data.merchantPayments
  newWorksheet.paymentPlanId = data.paymentPlanId
  newWorksheet.creditApplicationId = id
  newWorksheet.isHighEstimate = data.isHighEstimate
  newWorksheet.highEstimateExplainedToClient = data.highEstimateExplainedToClient

  return newWorksheet
}

export function updatePersonalLoanWorksheet(data: EditPersonalLoanWorksheetDto, creditApp: CreditApplication) {
  if (creditApp.worksheet) {
    creditApp.worksheet.amountRequested = data.amountRequested
    creditApp.worksheet.deliveryOn = formatDate(data.deliveryOn)
    creditApp.worksheet.firstPaymentOn = formatDate(data.firstPaymentOn)
    creditApp.worksheet.paymentFrequency = data.paymentFrequency
    creditApp.worksheet.term = data.term
    creditApp.worksheet.merchantPayments = data.merchantPayments
    creditApp.worksheet.paymentPlanId = data.paymentPlanId
    creditApp.worksheet.includeInsurance = data.includeInsurance
  }
  const worksheet = {
    ...creditApp.worksheet,
    // Keep the DateOnly form values on the returned DTO (the wire format is
    // identical); the string mutations above are only for creditApp.worksheet.
    deliveryOn: data.deliveryOn,
    firstPaymentOn: data.firstPaymentOn,
    isHighEstimate: data.isHighEstimate,
    highEstimateExplainedToClient: data.highEstimateExplainedToClient,
  } as EditPersonalLoanWorksheetDto
  return worksheet
}

export function createNewIfXpressWorksheetDto(id: string, data: EditIfXpressWorksheetDto) {
  const newWorksheet = {
    ...(EditIfXpressWorksheetDtoSchema.getDefault() as unknown as EditIfXpressWorksheetDto),
    includeInsurance: data.includeInsurance,
  } as EditIfXpressWorksheetDto
  newWorksheet.amountRequested = data.amountRequested
  newWorksheet.deliveryOn = data.deliveryOn
  newWorksheet.firstPaymentOn = data.firstPaymentOn
  newWorksheet.paymentFrequency = data.paymentFrequency
  newWorksheet.term = data.term
  newWorksheet.paymentPlanId = data.paymentPlanId
  newWorksheet.creditApplicationId = id
  newWorksheet.isHighEstimate = data.isHighEstimate
  newWorksheet.highEstimateExplainedToClient = data.highEstimateExplainedToClient

  return newWorksheet
}

export function updateIfXpressWorksheet(data: EditIfXpressWorksheetDto, creditApp: CreditApplication) {
  if (creditApp.worksheet) {
    creditApp.worksheet.amountRequested = data.amountRequested
    creditApp.worksheet.deliveryOn = formatDate(data.deliveryOn)
    creditApp.worksheet.firstPaymentOn = formatDate(data.firstPaymentOn)
    creditApp.worksheet.paymentFrequency = data.paymentFrequency
    creditApp.worksheet.term = data.term
    creditApp.worksheet.paymentPlanId = data.paymentPlanId
    creditApp.worksheet.includeInsurance = data.includeInsurance
  }
  const worksheet = {
    ...creditApp.worksheet,
    // Keep the DateOnly form values on the returned DTO (the wire format is
    // identical); the string mutations above are only for creditApp.worksheet.
    deliveryOn: data.deliveryOn,
    firstPaymentOn: data.firstPaymentOn,
    isHighEstimate: data.isHighEstimate,
    highEstimateExplainedToClient: data.highEstimateExplainedToClient,
  } as EditIfXpressWorksheetDto
  return worksheet
}

export const FormatPaymentPlanForSummary = (plan: MerchantPaymentPlan | undefined) => {
  if (plan) {
    return `${plan?.reducedInterestRate}%/${plan?.reducedRateDurationInMonths} → ${plan?.interestRate}%/${subtract(
      plan.loanTerm,
      plan.reducedRateDurationInMonths,
    )}`
  }
  return ''
}

export const FormatPaymentPlanForList = (plan: MerchantPaymentPlan | undefined) => {
  if (plan) {
    const trimmedPromoCode = plan.promoCode.replace('PLAN', '').trim()
    return `${trimmedPromoCode}→${plan.loanTerm}/${plan.reducedInterestRate}%/${plan.borrowerFeeRate}%/${plan.merchantFeeRate}%`
  }
  return ''
}

export const isVariableInterest = (paymentPlanId: string): boolean => {
  return Boolean(paymentPlanId) && paymentPlanId !== (EPaymentPlan.RegularDailyInterests as string)
}

/**
 * Total coverage cost, whichever form the API reported it in: iFinance protection
 * (the modern field) or legacy third-party insurance plus its provincial tax.
 * Mirrors BaseWorksheet.GetTotalCoverageAmount — the two are mutually exclusive,
 * so summing both is safe and survives the #64456 transition either way.
 */
export const getCoverageTotal = (
  funding: { insuranceFee?: number; insuranceTax?: number; protectionPlanAmount?: number } | null,
) => (funding?.protectionPlanAmount ?? 0) + (funding?.insuranceFee ?? 0) + (funding?.insuranceTax ?? 0)

/** Payments a term of `termMonths` produces at the given frequency. */
const getPaymentCount = (termMonths: number, paymentFrequency: EPaymentFrequency): number => {
  switch (paymentFrequency) {
    case EPaymentFrequency.BiWeekly:
      return Math.round((termMonths / 12) * 26)
    case EPaymentFrequency.Weekly:
      return Math.round((termMonths / 12) * 52)
    case EPaymentFrequency.Single:
      return 1
    default:
      return termMonths
  }
}

/**
 * The coverage premium spread evenly over the payments — the "+X per payment" the
 * worksheet shows beside the Protection Plan opt-in. An allocation of the premium,
 * not the exact difference the payment moves by: the premium is financed, so the
 * true delta also carries interest on it and runs slightly higher on any plan above
 * 0%. There is no per-payment figure to use instead — the funding compute reports
 * `protectionPlanAmount` as a term total.
 */
export const getCoveragePerPayment = (
  funding: { insuranceFee?: number; insuranceTax?: number; protectionPlanAmount?: number } | null,
  termMonths: number | null,
  paymentFrequency: EPaymentFrequency,
): number => {
  const total = getCoverageTotal(funding)
  if (total <= 0 || !termMonths) return 0
  return total / getPaymentCount(termMonths, paymentFrequency)
}

export const getTotalObligation = (
  amountRequested: number,
  computedPersonalLoanFunding: ComputeFundingResultDto | null,
) => {
  return (
    amountRequested +
    (computedPersonalLoanFunding?.totalInterestAmount ?? 0) +
    (computedPersonalLoanFunding?.lenderFee ?? 0) +
    getCoverageTotal(computedPersonalLoanFunding)
  )
}

export const getIfXpressTotalObligation = (
  amountRequested: number,
  computedPersonalLoanFunding: ComputeIfXpressFundingResultDto | null,
) => {
  return (
    amountRequested +
    (computedPersonalLoanFunding?.totalInterestAmount ?? 0) +
    (computedPersonalLoanFunding?.lenderFee ?? 0) +
    getCoverageTotal(computedPersonalLoanFunding)
  )
}

export const getPersonalFundingDto = (
  creditApplicationId: string,
  amountRequested: number,
  paymentFrequency: EPaymentFrequency,
  term: number,
  deliveryOn: string,
  firstPaymentOn: string,
  includeInsurance: boolean,
  stateIso: EProvince | null | undefined,
  paymentPlanId: string,
  interestRate: number,
  hasCoapplicant: boolean,
) => {
  return {
    creditApplicationId,
    amountRequested,
    paymentFrequency,
    term,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    // Credit API #64456: includeInsurance is obsolete and only ProtectionPlanAmount
    // is populated for iFinance coverage, so both flags are sent in step.
    includeProtection: includeInsurance,
    stateIso,
    paymentPlanId,
    interestRate,
    hasCoapplicant,
  } as ComputeFundingDto
}

export const getIfXpressFundingDto = (
  creditApplicationId: string,
  amountRequested: number,
  paymentFrequency: EPaymentFrequency,
  term: number,
  deliveryOn: string,
  firstPaymentOn: string,
  includeInsurance: boolean,
  stateIso: EProvince | null | undefined,
  paymentPlanId: string,
  interestRate: number,
  hasCoapplicant: boolean,
) => {
  return {
    creditApplicationId,
    amountRequested,
    paymentFrequency,
    term,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    // See getPersonalFundingDto — includeInsurance is obsolete (Credit API #64456).
    includeProtection: includeInsurance,
    stateIso,
    paymentPlanId,
    interestRate,
    hasCoapplicant,
  } as ComputeIfXpressFundingDto
}

export const getSelectedPlan = (merchant: Merchant, paymentPlanId: EPaymentPlan) => {
  return merchant?.paymentPlans?.find((p) => p.id === paymentPlanId)
}

export const isComputePersonalLoanDtoValid = (computePersonalLoanDto: ComputeFundingDto, maxAmountFinanced: number) => {
  try {
    const config = FinancingProgramConfigs[EFinancingProgram.Personal]
    buildWorksheetBaseSchema({ ...config, maxLoanAmount: maxAmountFinanced }).validateSync(computePersonalLoanDto)
    return true
  } catch (error) {
    reportErrorToConsole(error)
    return false
  }
}

export const isComputeIfXpressDtoValid = (computeIfXpressDto: ComputeIfXpressFundingDto) => {
  try {
    buildWorksheetBaseSchema(FinancingProgramConfigs[EFinancingProgram.IfXpress]).validateSync(computeIfXpressDto)
    return true
  } catch (error) {
    reportErrorToConsole(error)
    return false
  }
}

export const isComputePossibleTermsDtoValid = (
  monthTermFrequencyDto: ComputeMonthTermFrequencyDto,
  maxAmountFinanced: number,
) => {
  try {
    buildComputePossibleTermsDtoSchema(maxAmountFinanced).validateSync(monthTermFrequencyDto)
    return true
  } catch (error) {
    reportErrorToConsole(error)
    return false
  }
}

export const isComputePossibleTermsIfXpressDtoValid = (monthTermFrequencyDto: ComputeMonthTermFrequencyDto) => {
  try {
    computePossibleTermsIfXpressDtoSchema.validateSync(monthTermFrequencyDto)
    return true
  } catch (error) {
    reportErrorToConsole(error)
    return false
  }
}

export const getMerchantFees = (merchantFeeRate: number | undefined, amountRequested: number) => {
  if (merchantFeeRate) return formatCurrency((merchantFeeRate / 100) * amountRequested)
  return ''
}

export const isValidFirstPayment = (deliveryDateStr: string | Date, firstPaymentOn: string | Date) => {
  const minValidPaymentDate = addDays(normalizeDateTime(deliveryDateStr), 2)
  const normalizedFirstPaymentDate = normalizeDateTime(firstPaymentOn)
  return !isBefore(normalizedFirstPaymentDate, minValidPaymentDate)
}

export const isDeliveryOnValid = (deliveryOn: string): boolean => {
  const deliveryDate = new Date(deliveryOn)
  deliveryDate.setUTCHours(0, 0, 0, 0)
  const today = new Date()
  today.setUTCHours(0, 0, 0, 0)

  return deliveryDate >= today
}
